import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useResolvedDaycareHotelClinicId } from "@/hooks/useResolvedDaycareHotelClinicId";

type StayDailyRatesRow = {
  daycare_daily_rate: number | null;
  hotel_nightly_rate: number | null;
  hotel_nightly_rate_with_daycare_plan: number | null;
};

export interface StayDailyRates {
  daycareDailyRate: number | null;
  hotelNightlyRate: number | null;
  /** Nulo = a clínica não diferencia; todo mundo paga a faixa padrão. */
  hotelNightlyRateWithDaycarePlan: number | null;
}

export interface StayDailyRatesInput {
  daycareDailyRate?: number | null;
  hotelNightlyRate?: number | null;
  hotelNightlyRateWithDaycarePlan?: number | null;
}

export const EMPTY_STAY_DAILY_RATES: StayDailyRates = {
  daycareDailyRate: null,
  hotelNightlyRate: null,
  hotelNightlyRateWithDaycarePlan: null,
};

export function normalizeStayDailyRates(row: StayDailyRatesRow | null): StayDailyRates {
  if (!row) return EMPTY_STAY_DAILY_RATES;
  return {
    daycareDailyRate: row.daycare_daily_rate,
    hotelNightlyRate: row.hotel_nightly_rate,
    hotelNightlyRateWithDaycarePlan: row.hotel_nightly_rate_with_daycare_plan ?? null,
  };
}

export const stayDailyRatesKey = (clinicId: string | null) => ["stay-daily-rates", clinicId];

export function useStayDailyRates() {
  const { clinicId, isLoading: isLoadingClinicId } = useResolvedDaycareHotelClinicId();

  return useQuery({
    queryKey: stayDailyRatesKey(clinicId),
    enabled: Boolean(clinicId) && !isLoadingClinicId,
    queryFn: async (): Promise<StayDailyRates> => {
      if (!clinicId) return EMPTY_STAY_DAILY_RATES;
      const result = await (supabase.from("stay_daily_rates" as never) as any)
        .select("daycare_daily_rate, hotel_nightly_rate, hotel_nightly_rate_with_daycare_plan")
        .eq("clinic_id", clinicId)
        .maybeSingle() as { data: StayDailyRatesRow | null; error: unknown };
      if (result.error) throw result.error;
      return normalizeStayDailyRates(result.data);
    },
  });
}

export function useSaveStayDailyRates() {
  const { clinicId } = useResolvedDaycareHotelClinicId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StayDailyRatesInput) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const { error } = await (supabase.from("stay_daily_rates" as never) as any).upsert({
        clinic_id: clinicId,
        ...(input.daycareDailyRate !== undefined ? { daycare_daily_rate: input.daycareDailyRate } : {}),
        ...(input.hotelNightlyRate !== undefined ? { hotel_nightly_rate: input.hotelNightlyRate } : {}),
        ...(input.hotelNightlyRateWithDaycarePlan !== undefined
          ? { hotel_nightly_rate_with_daycare_plan: input.hotelNightlyRateWithDaycarePlan }
          : {}),
      }, { onConflict: "clinic_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayDailyRatesKey(clinicId) });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
}
